import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Menu, X, MessageCircle } from "lucide-react";
import { NAV_LINKS } from "../../data";

export const scrollToId = (href) => {
  const el = document.querySelector(href);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -70 });
  else el.scrollIntoView({ behavior: "smooth" });
};

const HREF_TO_PATH = {
  "#home": "/", "#about": "/about", "#journey": "/journey", "#vision": "/vision",
  "#service": "/public-service", "#updates": "/updates", "#gallery": "/gallery", "#contact": "/contact",
};

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    navigate(HREF_TO_PATH[href] || "/");
    scrollToId(href);
  };

  return (
    <>
      <motion.header
        data-testid="site-navbar"
        initial={{ y: -80 }} animate={{ y: 0 }} transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed top-0 left-0 right-0 z-50 transition-[background,box-shadow,padding] duration-500 ${
          scrolled ? "glass-dark py-3 shadow-[0_10px_40px_rgba(10,25,47,0.25)]" : "py-5 bg-transparent"
        }`}
      >
        <nav className="container-x flex items-center justify-between">
          <button data-testid="nav-logo" onClick={() => go("#home")}
            className="font-display font-bold text-lg tracking-tight text-ivory">
            <span style={{ color: "var(--ivory)" }}>PUTT</span><span style={{ color: "var(--gold)" }}>RAJ</span>
          </button>

          <ul className="hidden lg:flex items-center gap-7">
            {NAV_LINKS.map((l) => (
              <li key={l.href}>
                <button data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                  onClick={() => go(l.href)}
                  className="font-body text-[13px] tracking-wide text-ivory/80 hover:text-white transition-colors duration-300 relative group">
                  {l.label}
                  <span className="absolute -bottom-1 left-0 h-px w-0 bg-[var(--gold)] group-hover:w-full transition-[width] duration-300" />
                </button>
              </li>
            ))}
          </ul>

          <div className="flex items-center gap-3">
            <button data-testid="nav-connect-btn" onClick={() => go("#contact")}
              className="btn-primary hidden sm:inline-flex items-center gap-2 rounded-full font-body font-semibold text-[13px] px-5 py-2.5">
              <MessageCircle size={15} /> Citizen Help
            </button>
            <button data-testid="nav-mobile-toggle" onClick={() => setOpen(true)}
              className="lg:hidden text-ivory p-2" aria-label="Open menu">
              <Menu size={24} color="var(--ivory)" />
            </button>
          </div>
        </nav>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div data-testid="mobile-menu"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] lg:hidden"
            style={{ background: "var(--navy)" }}>
            <div className="flex justify-between items-center container-x py-6">
              <span className="font-display font-bold text-lg text-ivory">PUTT<span style={{ color: "var(--gold)" }}>RAJ</span></span>
              <button data-testid="mobile-menu-close" onClick={() => setOpen(false)} aria-label="Close menu">
                <X size={28} color="var(--ivory)" />
              </button>
            </div>
            <ul className="flex flex-col px-8 mt-6 gap-1">
              {NAV_LINKS.map((l, i) => (
                <motion.li key={l.href}
                  initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.06 * i }}>
                  <button onClick={() => go(l.href)}
                    className="font-display text-3xl font-medium py-3 text-ivory/90 hover:text-[var(--gold)] transition-colors">
                    {l.label}
                  </button>
                </motion.li>
              ))}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
